import { FC, useEffect, useState } from 'react';

import { useCms } from '@eneco/cms';
import { type Link, linkWithUrl } from '@eneco/cms-types';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { typedYupResolver } from '@eneco/hooks/form';
import { ButtonLink, Heading, Image, NotificationBox, RadioTile, Stack, Stretch, TextLink } from '@eneco/sparky';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { ChoiceStepFooter, FlowRadioGroup, Header, Layout, getRadioGroupNameProps } from '../../components';
import { requiredRadioChoice } from '../../helpers/requiredRadioChoice';
import { useFlowRedirect, useStepContent, useStepKey } from '../../hooks';
import { FlowContext } from '../../types';
import { useFlowHooks } from '../../utils/FlowProvider';
import { getIllustration } from '../../utils/getIllustration';
import { readChoiceOptions } from '../../utils/readChoiceOptions';
import { readContentSection } from '../../utils/readContentSection';
import { readMoreInfoDialogs } from '../../utils/readMoreInfoDialogs';

interface FormFields {
  [key: string]: string;
}

/** Where an option keeps the link it redirects to, in either shape its options come in (see `readChoiceOptions`). */
interface OptionLinkFields {
  genericContentRadio?: { options?: { name?: string; redirectLink?: Link }[] };
  items?: { itemName?: string; redirectLink?: Link }[];
}

/** The link of the option that records `answer`. */
const redirectLinkOf = (fields: OptionLinkFields | undefined, answer: string): Link | undefined =>
  fields?.genericContentRadio?.options?.find(option => option.name === answer)?.redirectLink ??
  fields?.items?.find(item => item.itemName === answer)?.redirectLink;

export const GenericRadioChoiceStep = () => {
  // This step won't re-render properly when used on multiple consecutive steps
  // without a key that changes per step page. See `useStepKey` for why the
  // rendering's `uid` is not that key.
  return <GenericRadioChoice key={useStepKey()} />;
};

const GenericRadioChoice: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks();
  const sendToFlowMachine = useFlowActorRef().send;
  const flowContext = useFlowSelector(state => state.context);
  const { customRedirect } = useFlowRedirect();
  const { adapter } = useCms();
  const textualData = useStepContent('GenericRadioChoiceStep');
  const { title, body } = readContentSection(textualData);
  const { options, requiredMessage: radioGroupMessage } = readChoiceOptions(textualData);
  const { fieldDialog: primaryDialog, sectionDialog: fallbackDialog } = readMoreInfoDialogs(textualData);

  const genericFieldName = textualData?.name ?? '';
  const enableRedirectLinkFlag = textualData?.enableRedirectLinkFlag;
  // The step's own message wins over the radio group's, as it always has here.
  const requiredMessage = textualData?.requiredMessageText || radioGroupMessage;

  const formSchema = yup.object({
    [genericFieldName]: requiredRadioChoice(requiredMessage),
  });

  const {
    handleSubmit,
    control,
    getValues,
    watch,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    defaultValues: {
      [genericFieldName]: flowContext[genericFieldName as keyof FlowContext]?.toString(),
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const [isPrimaryModalOpen, setIsPrimaryModalOpen] = useState(false);
  const [isFallbackModalOpen, setIsFallbackModalOpen] = useState(false);

  const mutatedGenericField = watch(genericFieldName);
  const fallbackImage = adapter.toImage(fallbackDialog.image);
  const fallbackButtonLink = linkWithUrl(fallbackDialog.buttonLink);

  const showNonBlockingNotification =
    mutatedGenericField === textualData?.nonBlockingValue && textualData?.nonBlockingChoiceNotification?.content;

  const showBlockingNotification =
    mutatedGenericField === textualData?.blockingValue && textualData?.errorBlockingChoiceNotification?.content;

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formData = getValues();
      let genericFieldValue: string | boolean | number = formData[genericFieldName];

      // Check if redirectLink should be used
      if (enableRedirectLinkFlag) {
        const redirectHref = redirectLinkOf(textualData, genericFieldValue)?.href;

        if (redirectHref) {
          customRedirect(redirectHref, { cleanBasket: false });
          return;
        }
      }

      // Convert genericFieldValue to the appropriate type
      if (genericFieldValue.toLowerCase() === 'true' || genericFieldValue.toLowerCase() === 'false') {
        genericFieldValue = genericFieldValue.toLowerCase() === 'true';
      } else if (Number(genericFieldValue) || Number(genericFieldValue) === 0) {
        genericFieldValue = Number(genericFieldValue);
      }

      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          [genericFieldName]: genericFieldValue,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <Header>
        {title && <Header.Title>{title}</Header.Title>}

        {(body || primaryDialog?.triggerText || fallbackDialog.triggerText) && (
          <Header.Text>
            <Stack gap="3" direction="column">
              <RichText html={body} />

              {primaryDialog?.title && primaryDialog?.title.length > 0 ? (
                // Show Primary Dialog if title length is greater than 0
                <TrackedDialog
                  isOpen={isPrimaryModalOpen}
                  setOpen={setIsPrimaryModalOpen}
                  trigger={<TextLink emphasis="low">{primaryDialog.triggerText}</TextLink>}>
                  <Stack gap="6" direction="column">
                    <Heading size="S" as="h2">
                      {primaryDialog.title}
                    </Heading>
                    <RichText html={primaryDialog.content} />
                  </Stack>
                </TrackedDialog>
              ) : (
                // Show Fallback Dialog if Primary Dialog title is empty or not defined
                fallbackDialog.triggerText && (
                  <TrackedDialog
                    isOpen={isFallbackModalOpen}
                    setOpen={setIsFallbackModalOpen}
                    trigger={<TextLink emphasis="low">{fallbackDialog.triggerText}</TextLink>}>
                    <Stack gap="6" direction="column">
                      {fallbackImage && (
                        <Image
                          src={fallbackImage.src}
                          alt={fallbackImage.alt || ''}
                          aspectRatio="16/9"
                          height="auto"
                          objectFit="cover"
                        />
                      )}
                      {fallbackDialog.title && (
                        <Heading size="S" as="h2">
                          {fallbackDialog.title}
                        </Heading>
                      )}
                      <RichText html={fallbackDialog.text} />
                      {fallbackButtonLink && (
                        <Stack.Item>
                          <Stretch width={{ initial: true, lg: 'auto' }}>
                            <ButtonLink
                              action="primary"
                              target={fallbackButtonLink.target}
                              href={fallbackButtonLink.href}>
                              {fallbackButtonLink.text}
                            </ButtonLink>
                          </Stretch>
                        </Stack.Item>
                      )}
                    </Stack>
                  </TrackedDialog>
                )
              )}
            </Stack>
          </Header.Text>
        )}
      </Header>

      {textualData?.notification?.content && (
        <NotificationBox
          isAlert={false}
          variant={textualData.notification.variant}
          title={textualData.notification.title}
          text={<RichText html={textualData.notification.content} />}
        />
      )}

      <FlowRadioGroup
        control={control}
        name={genericFieldName}
        {...getRadioGroupNameProps(textualData?.radioGroupLabel)}
        // Rich text in both CMSs: as a plain string, its markup would show as text.
        hint={textualData?.helpText ? <RichText html={textualData.helpText} /> : undefined}
        direction="column"
        error={errors?.[genericFieldName]?.message}>
        {options.map(({ name, label, illustration }) => {
          const Illustration = getIllustration(illustration);

          return (
            <RadioTile
              key={name}
              value={name ?? ''}
              icon={Illustration && <Illustration size="small" color="currentColor" />}>
              {label ?? ''}
            </RadioTile>
          );
        })}
      </FlowRadioGroup>

      <ChoiceStepFooter
        nonBlockingNotification={textualData?.nonBlockingChoiceNotification}
        blockingNotification={textualData?.errorBlockingChoiceNotification}
        isNonBlockingShown={Boolean(showNonBlockingNotification)}
        isBlocked={Boolean(showBlockingNotification)}
        nextStepText={textualData?.nextStepText}
        isLoading={isSubmitSuccessful}
      />
    </Layout.Content>
  );
};
